import { Lang } from './app.service';

/**
 * One selectable interface language. Native script renders in the language's own writing system,
 * with the English name + region underneath and a flag emoji as the primary visual cue.
 *
 * This is the single source of truth for the app's language catalogue — the topbar language
 * switcher renders it, and any DATA-translation surface (e.g. the Suppliers & Channels name
 * translations) offers the same set so an editor can only translate into a language the UI ships.
 */
export interface AppLanguage {
  code: Lang;
  native: string;
  name: string;
  region: string;
  /** Country flag emoji — best-represents the language's main user base (Arabic → 🇸🇦, English → 🇬🇧). */
  flag: string;
  /** Writing direction — drives `dir` on native text so Persian / Arabic read RTL. */
  dir: 'ltr' | 'rtl';
  rtl: boolean;
}

/** Iran-anchored OTA catalogue — six languages, two of which (Persian / Arabic) mirror the whole
 *  interface to RTL. Display order (user-set): English, French, Persian, Turkish, Russian, Arabic. */
export const APP_LANGUAGES: readonly AppLanguage[] = [
  { code: 'en', native: 'English',  name: 'English', region: 'Default · LTR', flag: '🇬🇧', dir: 'ltr', rtl: false },
  { code: 'fr', native: 'Français', name: 'French',  region: 'France',        flag: '🇫🇷', dir: 'ltr', rtl: false },
  { code: 'fa', native: 'فارسی',    name: 'Persian', region: 'ایران',         flag: '🇮🇷', dir: 'rtl', rtl: true  },
  { code: 'tr', native: 'Türkçe',   name: 'Turkish', region: 'Türkiye',       flag: '🇹🇷', dir: 'ltr', rtl: false },
  { code: 'ru', native: 'Русский',  name: 'Russian', region: 'Россия',        flag: '🇷🇺', dir: 'ltr', rtl: false },
  { code: 'ar', native: 'العربية',  name: 'Arabic',  region: 'الخليج',        flag: '🇸🇦', dir: 'rtl', rtl: true  },
];

/** Code → flag lookup. */
export const LANG_FLAGS: Record<Lang, string> = Object.fromEntries(
  APP_LANGUAGES.map((l) => [l.code, l.flag]),
) as Record<Lang, string>;
